import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/App';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  ClipboardList,
  Database,
  CheckSquare,
  BarChart3,
  LogOut,
  Menu,
  X,
  Settings,
  User,
  ChevronDown,
  Users,
  Download,
  Wrench,
  History,
  FileCheck,
  ClipboardCheck
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [approvalsCount, setApprovalsCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const handler = () => setRefreshTrigger((t) => t + 1);
    window.addEventListener('approvals-changed', handler);
    return () => window.removeEventListener('approvals-changed', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const getHeaderCount = (res) => {
      if (!res || !res.headers) return null;
      const raw = res.headers['x-total-count'] ?? res.headers['X-Total-Count'];
      if (raw == null) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    };

    const getArrayCount = (res) =>
      Array.isArray(res?.data) ? res.data.length : 0;

    async function fetchCounts() {
      try {
        const [runsRes, tiRes] = await Promise.all([
          api.get('/production-runs', { params: { status: 'SUBMITTED' } }),
          api.get('/shift-logs', { params: { status: 'Submitted' } }),
        ]);
        if (cancelled) return;

        let runsCount = getHeaderCount(runsRes);
        let tiCount = getHeaderCount(tiRes);

        // Prefer header; if missing, optionally hit dedicated count endpoints
        if (runsCount == null || tiCount == null) {
          try {
            const [runsCountRes, tiCountRes] = await Promise.all([
              api.get('/production-runs/count', { params: { status: 'SUBMITTED' } }),
              api.get('/shift-logs/count', { params: { status: 'Submitted' } }),
            ]);

            if (!cancelled) {
              if (runsCount == null && typeof runsCountRes?.data?.count === 'number') {
                runsCount = runsCountRes.data.count;
              }
              if (tiCount == null && typeof tiCountRes?.data?.count === 'number') {
                tiCount = tiCountRes.data.count;
              }
            }
          } catch {
            // Ignore count endpoint errors and fall back to array length below
          }
        }

        if (runsCount == null) runsCount = getArrayCount(runsRes);
        if (tiCount == null) tiCount = getArrayCount(tiRes);

        setApprovalsCount((runsCount || 0) + (tiCount || 0));
      } catch {
        if (!cancelled) setApprovalsCount(0);
      }
    }
    fetchCounts();

    // Keep counts fresh while user is in the app
    intervalId = setInterval(fetchCounts, 30000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [refreshTrigger]);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'] },
    { path: '/approvals', label: 'Approvals', icon: CheckSquare, roles: ['OWNER', 'MANAGER'] },
    { path: '/production-runs', label: 'Production Entry', icon: ClipboardList, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'], end: true },
    { path: '/production-runs/history', label: 'Production History', icon: History, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'], end: true },
    { path: '/tool-inspection', label: 'Tool Inspection', icon: Wrench, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'], end: true },
    { path: '/tool-inspection/history', label: 'Tool Insp History', icon: History, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'], end: true },
    { path: '/tool-life-standards', label: 'Tool Life Standards', icon: FileCheck, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'] },
    { path: '/inprocess-inspection', label: 'In-Process Inspection', icon: ClipboardCheck, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'], end: true },
    { path: '/master-data', label: 'Master Data', icon: Database, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'] },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, roles: ['OWNER', 'MANAGER', 'SUPERVISOR'] },
    { path: '/export', label: 'Export Data', icon: Download, roles: ['OWNER', 'MANAGER'] },
    { path: '/user-management', label: 'User Management', icon: Users, roles: ['OWNER', 'MANAGER'] },
  ];

  /** Supervisors: original stack (Analytics after Master Data, no Approvals/Export/User mgmt). */
  const navOrderSupervisor = [
    '/dashboard',
    '/production-runs',
    '/production-runs/history',
    '/tool-inspection',
    '/tool-inspection/history',
    '/tool-life-standards',
    '/inprocess-inspection',
    '/master-data',
    '/analytics',
  ];

  /** Owners & managers: Approvals 2nd, Analytics 3rd, Export 4th, then operational entries, then Master Data, then User Management. */
  const navOrderOwnerManager = [
    '/dashboard',
    '/approvals',
    '/analytics',
    '/master-data',
    '/export',
    '/production-runs',
    '/production-runs/history',
    '/tool-inspection',
    '/tool-inspection/history',
    '/tool-life-standards',
    '/inprocess-inspection',
    '/user-management',
  ];

  const filteredNavItems = navItems
    .filter((item) => item.roles.includes(user?.role))
    .sort((a, b) => {
      const order = user?.role === 'SUPERVISOR' ? navOrderSupervisor : navOrderOwnerManager;
      const ia = order.indexOf(a.path);
      const ib = order.indexOf(b.path);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'OWNER': return 'bg-purple-100 text-purple-800';
      case 'MANAGER': return 'bg-blue-100 text-blue-800';
      case 'SUPERVISOR': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex h-screen min-h-0 flex-col bg-white border-r border-slate-200 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex shrink-0 items-center justify-between px-4 border-b border-slate-200">
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              {/* Logo placeholder - replace src with actual logo */}
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden" data-testid="company-logo">
                {/* Uncomment and update src when logo is available:
                <img src="/logo.png" alt="Swastik Udyog" className="w-full h-full object-contain" />
                */}
                <span className="font-bold text-primary text-lg">S</span>
              </div>
              <span className="font-heading font-bold text-lg text-slate-900">Swastik Udyog</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
            className="h-10 w-10"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="min-h-0 flex-1 overflow-y-auto p-4 space-y-2">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100'
                }`
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <div className="flex items-center justify-between flex-1">
                  <span>{item.label}</span>
                  {item.path === '/approvals' && approvalsCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs min-w-[1.5rem] px-1.5 py-0.5">
                      {approvalsCount}
                    </span>
                  )}
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User section at bottom */}
        <div className="shrink-0 border-t border-slate-200 bg-white p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu"
                className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 transition-colors ${
                  sidebarOpen ? '' : 'justify-center'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                {sidebarOpen && (
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm text-slate-900 truncate">{user?.name}</p>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                      {user?.role}
                    </span>
                  </div>
                )}
                {sidebarOpen && <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-slate-600">
                <User className="w-4 h-4 mr-2" />
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Header */}
        <header className="h-16 glass-header sticky top-0 z-40 flex items-center justify-between px-6">
          <div></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
